/**
 * CANONICAL MODULE — app-platform/shared/idbBackup.ts
 *
 * Copied byte-for-byte into every app that has an IndexedDB store (`src/lib/idbBackup.ts`; trakr has
 * none and does not take it). Raw IndexedDB, no Dexie import, the `IDBFactory` injected (the real
 * `indexedDB` in the app, `fake-indexeddb` in tests). Built for work order W12 (task 2.b) from
 * [design/W12-storage.md](../design/W12-storage.md) §3.2.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── What it guarantees ───────────────────────────────────────────────────────────────────────────
 * - **A backup is trusted only when its `complete` marker exists, and the marker is written LAST**,
 *   in its own transaction after every row has committed. A copy killed midway has no marker: it is
 *   recognised (`readBackupMarker` → `complete: false`), redone on the next run, never restored
 *   from (`restoreBackup` throws `IncompleteBackupError`).
 * - **The schema travels with the rows** (keyPath, autoIncrement, every index), so a restore
 *   recreates the store exactly as it was at the source version, which Dexie then opens normally.
 * - **A restore killed midway is safe to redo.** It records `restoring` in the backup before it
 *   replaces the target and clears it after; while it is set, `copyDatabase` refuses to overwrite
 *   that backup, and `restoreBackup` from the same, still-complete backup redoes the restore. The
 *   backup is never deleted by a restore. (A `pendingRestore` finder existed until 2026-11-01; no
 *   app called it, so it was deleted, task 8.3: `readBackupMarker(...).restoring` answers the same.)
 * - Opening a database that does not exist creates it, so every existence check here goes through
 *   `factory.databases()` first.
 *
 * Names: `${store}~pre-v${version}` (a backup), `${store}~dry-v${version}` (the runner's scratch copy).
 */

import type { BackupHandle, Census, MigrationIo, MigrationJournal, StoreSchema } from './migrations'

const META_STORE = '__backup_meta'


export interface BackupMarker {
  complete: true
  source: string
  sourceVersion: number
  at: string
  counts: Record<string, number>
  schema: StoreSchema[]
}

/** Exported for tests only (app-platform 8.3): no app code calls it. */
export class IncompleteBackupError extends Error {
  readonly backup: string
  constructor(backup: string) {
    super(`idbBackup: ${backup} has no completion marker; it is never restored from.`)
    this.backup = backup
    this.name = 'IncompleteBackupError'
  }
}

/** Exported for tests only (app-platform 8.3): no app code calls it. */
export function backupName(store: string, tag: string): string {
  return `${store}~${tag}`
}

// ------------------------------------------------------------------------------------ plumbing

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'))
  })
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
  })
}

export async function databaseExists(factory: IDBFactory, name: string): Promise<boolean> {
  if (typeof factory.databases !== 'function') {
    throw new Error('idbBackup: this browser cannot list databases; refusing to guess')
  }
  return (await factory.databases()).some((d) => d.name === name)
}

/** Open an EXISTING database at its current version (no upgrade). `null` if it does not exist. */
export async function openExisting(factory: IDBFactory, name: string): Promise<IDBDatabase | null> {
  if (!(await databaseExists(factory, name))) return null
  return req(factory.open(name))
}

export function deleteDatabase(factory: IDBFactory, name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const r = factory.deleteDatabase(name)
    r.onsuccess = () => resolve()
    r.onerror = () => reject(r.error ?? new Error(`could not delete ${name}`))
    // `blocked` resolves later, when the other connection closes; nothing to do here.
  })
}

function schemaOf(db: IDBDatabase): StoreSchema[] {
  const names = Array.from(db.objectStoreNames).filter((n) => n !== META_STORE)
  if (names.length === 0) return []
  const tx = db.transaction(names, 'readonly')
  return names.map((n) => {
    const s = tx.objectStore(n)
    return {
      name: n,
      keyPath: s.keyPath === null ? null : (s.keyPath as string | string[]),
      autoIncrement: s.autoIncrement,
      indexes: Array.from(s.indexNames).map((iname) => {
        const i = s.index(iname)
        return { name: i.name, keyPath: i.keyPath as string | string[], unique: i.unique, multiEntry: i.multiEntry }
      }),
    }
  })
}

async function readAll(db: IDBDatabase, store: string): Promise<{ keys: IDBValidKey[]; values: unknown[] }> {
  const tx = db.transaction(store, 'readonly')
  const s = tx.objectStore(store)
  const [keys, values] = await Promise.all([req(s.getAllKeys()), req(s.getAll())])
  return { keys, values }
}

function createStores(db: IDBDatabase, schema: readonly StoreSchema[]): void {
  for (const st of schema) {
    const opts: IDBObjectStoreParameters = { autoIncrement: st.autoIncrement }
    if (st.keyPath !== null) opts.keyPath = st.keyPath
    const os = db.createObjectStore(st.name, opts)
    for (const i of st.indexes) os.createIndex(i.name, i.keyPath, { unique: i.unique, multiEntry: i.multiEntry })
  }
}

/** Create `name` at `version` with `schema` (+ the meta store when `withMeta`). Fails if it exists. */
async function createDatabase(
  factory: IDBFactory,
  name: string,
  version: number,
  schema: readonly StoreSchema[],
  withMeta: boolean,
): Promise<IDBDatabase> {
  if (await databaseExists(factory, name)) throw new Error(`idbBackup: ${name} already exists`)
  return new Promise((resolve, reject) => {
    const r = factory.open(name, Math.max(1, version))
    r.onupgradeneeded = () => {
      const db = r.result
      createStores(db, schema)
      if (withMeta) db.createObjectStore(META_STORE)
    }
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error ?? new Error(`could not create ${name}`))
  })
}

async function writeRows(db: IDBDatabase, schema: readonly StoreSchema[], rows: Map<string, { keys: IDBValidKey[]; values: unknown[] }>, hooks?: CopyHooks): Promise<void> {
  for (const st of schema) {
    const data = rows.get(st.name)
    if (!data || data.values.length === 0) continue
    const tx = db.transaction(st.name, 'readwrite')
    const os = tx.objectStore(st.name)
    data.values.forEach((v, i) => {
      if (st.keyPath === null) os.put(v, data.keys[i])
      else os.put(v)
    })
    await done(tx)
    await hooks?.afterStore?.(st.name)
  }
}

// ------------------------------------------------------------------------------------ backups

export interface CopyHooks {
  /** Test seam: called after each store's rows commit (a throw here is a kill mid-copy). */
  afterStore?(store: string): void | Promise<void>
}

/**
 * Copy `source` into `target`, schema and rows, then the marker last. An existing target WITHOUT a
 * marker is a killed earlier copy: it is deleted and redone. An existing COMPLETE target is replaced
 * too (it may predate edits made since) unless `reuseComplete` is set. The real store is untouched
 * throughout, so the moment between deleting an old backup and finishing the new one loses nothing.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export async function copyDatabase(
  factory: IDBFactory,
  source: string,
  target: string,
  opts: { now?: () => string; hooks?: CopyHooks; reuseComplete?: boolean } = {},
): Promise<BackupHandle & { marker: BackupMarker }> {
  const existing = await readBackupMarker(factory, target)
  if (existing.exists && existing.marker && opts.reuseComplete) return { name: target, complete: true, marker: existing.marker }
  if (existing.exists && existing.restoring) throw new Error(`idbBackup: ${target} is mid-restore; finish that first`)
  if (existing.exists) await deleteDatabase(factory, target)

  const src = await openExisting(factory, source)
  if (!src) throw new Error(`idbBackup: ${source} does not exist`)
  let schema: StoreSchema[]
  let version: number
  const rows = new Map<string, { keys: IDBValidKey[]; values: unknown[] }>()
  try {
    schema = schemaOf(src)
    version = src.version
    for (const st of schema) rows.set(st.name, await readAll(src, st.name))
  } finally {
    src.close()
  }
  const dst = await createDatabase(factory, target, version, schema, true)
  try {
    await writeRows(dst, schema, rows, opts.hooks)
    const counts: Record<string, number> = {}
    for (const st of schema) counts[st.name] = rows.get(st.name)?.values.length ?? 0
    const marker: BackupMarker = {
      complete: true,
      source,
      sourceVersion: version,
      at: (opts.now ?? (() => new Date().toISOString()))(),
      counts,
      schema,
    }
    // LAST, in its own transaction: only now is the copy a backup.
    const tx = dst.transaction(META_STORE, 'readwrite')
    tx.objectStore(META_STORE).put(marker, 'marker')
    await done(tx)
    return { name: target, complete: true, marker }
  } finally {
    dst.close()
  }
}

/** Exported for tests only (app-platform 8.3): no app code calls it. */
export async function readBackupMarker(
  factory: IDBFactory,
  name: string,
): Promise<{ exists: boolean; marker: BackupMarker | null; restoring: string | null }> {
  const db = await openExisting(factory, name)
  if (!db) return { exists: false, marker: null, restoring: null }
  try {
    if (!db.objectStoreNames.contains(META_STORE)) return { exists: true, marker: null, restoring: null }
    const tx = db.transaction(META_STORE, 'readonly')
    const os = tx.objectStore(META_STORE)
    const [marker, restoring] = await Promise.all([req(os.get('marker')), req(os.get('restoring'))])
    const m = marker && typeof marker === 'object' && (marker as { complete?: unknown }).complete === true ? (marker as BackupMarker) : null
    return { exists: true, marker: m, restoring: typeof restoring === 'string' ? restoring : null }
  } finally {
    db.close()
  }
}

export interface BackupListing {
  name: string
  complete: boolean
  marker: BackupMarker | null
}

/** Every `${store}~…` database on the device (for the quota panel, retention and resumes). */
export async function listBackups(factory: IDBFactory, store: string): Promise<BackupListing[]> {
  if (typeof factory.databases !== 'function') return []
  const names = (await factory.databases()).map((d) => d.name).filter((n): n is string => !!n && n.startsWith(`${store}~`))
  const out: BackupListing[] = []
  for (const name of names.sort()) {
    const m = await readBackupMarker(factory, name)
    out.push({ name, complete: m.marker !== null, marker: m.marker })
  }
  return out
}

async function setMeta(factory: IDBFactory, name: string, key: string, value: unknown): Promise<void> {
  const db = await openExisting(factory, name)
  if (!db) throw new Error(`idbBackup: ${name} vanished`)
  try {
    const tx = db.transaction(META_STORE, 'readwrite')
    if (value === undefined) tx.objectStore(META_STORE).delete(key)
    else tx.objectStore(META_STORE).put(value, key)
    await done(tx)
  } finally {
    db.close()
  }
}

/**
 * Replace `target` with the backup's rows at the backup's source version. Refuses an incomplete
 * backup. The backup is kept. `beforeReplace` / `hooks` are test seams for a kill midway.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export async function restoreBackup(
  factory: IDBFactory,
  backup: string,
  target: string,
  opts: { hooks?: CopyHooks } = {},
): Promise<void> {
  const m = await readBackupMarker(factory, backup)
  if (!m.marker) throw new IncompleteBackupError(backup)
  const marker = m.marker
  const src = await openExisting(factory, backup)
  if (!src) throw new IncompleteBackupError(backup)
  const rows = new Map<string, { keys: IDBValidKey[]; values: unknown[] }>()
  try {
    for (const st of marker.schema) rows.set(st.name, await readAll(src, st.name))
  } finally {
    src.close()
  }
  for (const st of marker.schema) {
    const got = rows.get(st.name)?.values.length ?? 0
    if (got !== (marker.counts[st.name] ?? 0)) throw new IncompleteBackupError(backup)
  }
  // Recorded before the target is touched, cleared after: a kill in between is resumable.
  await setMeta(factory, backup, 'restoring', target)
  if (await databaseExists(factory, target)) await deleteDatabase(factory, target)
  const dst = await createDatabase(factory, target, marker.sourceVersion, marker.schema, false)
  try {
    await writeRows(dst, marker.schema, rows, opts.hooks)
  } finally {
    dst.close()
  }
  await setMeta(factory, backup, 'restoring', undefined)
}

// ------------------------------------------------------------------------------------ census

/**
 * Row counts per store of an existing database (`META_STORE` excluded).
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export async function countRows(factory: IDBFactory, name: string): Promise<{ version: number; tables: Record<string, number> } | null> {
  const db = await openExisting(factory, name)
  if (!db) return null
  try {
    const stores = Array.from(db.objectStoreNames).filter((n) => n !== META_STORE)
    const tables: Record<string, number> = {}
    if (stores.length > 0) {
      const tx = db.transaction(stores, 'readonly')
      for (const s of stores) tables[s] = await req(tx.objectStore(s).count())
    }
    return { version: db.version, tables }
  } finally {
    db.close()
  }
}

/**
 * The runner's I/O for one IndexedDB store. The app supplies only how to open a database by NAME at
 * a target version (Dexie: `new AppDb(name).open()`; raw: `indexedDB.open(name, v)` with its
 * upgrade), and how to count unsynced work in an open database. Probing never upgrades; there is no
 * delete of the real store anywhere in here.
 */
export function createIdbMigrationIo(deps: {
  factory: IDBFactory
  name: string
  /** Open `dbName` at `target` (running its upgrades), then close it. */
  openAt(dbName: string, target: number): Promise<void>
  /** Unsynced work in `dbName` (dirty rows + queue + open conflicts). Called on an existing DB only. */
  unsynced(dbName: string): Promise<number>
  /** IndexedDB version → app schema version. Dexie stores `version * 10`; raw IDB is 1:1. */
  fromIdbVersion?: (v: number) => number
  now?: () => string
}): MigrationIo {
  const toApp = deps.fromIdbVersion ?? ((v: number) => v)
  const census = async (dbName: string): Promise<{ version: number; census: Census }> => {
    const c = await countRows(deps.factory, dbName)
    if (!c) throw new Error(`idbBackup: ${dbName} does not exist`)
    return { version: toApp(c.version), census: { tables: c.tables, unsynced: await deps.unsynced(dbName) } }
  }
  return {
    async probe() {
      const c = await census(deps.name)
      return { version: c.version, census: c.census, unsynced: c.census.unsynced }
    },
    async backup(tag) {
      const h = await copyDatabase(deps.factory, deps.name, backupName(deps.name, tag), deps.now ? { now: deps.now } : {})
      return { name: h.name, complete: h.complete }
    },
    apply: (target, against) => deps.openAt(against ? against.name : deps.name, target),
    census: async (against) => (await census(against ? against.name : deps.name)).census,
    restore: (b) => restoreBackup(deps.factory, b.name, deps.name),
    discard: (b) => deleteDatabase(deps.factory, b.name),
    // The journal lives beside the backup it refers to: a journal is only ever written once a
    // complete backup exists, and is what lets a run killed between apply and verify be checked.
    async readJournal() {
      for (const b of await listBackups(deps.factory, deps.name)) {
        if (!b.complete) continue
        const db = await openExisting(deps.factory, b.name)
        if (!db) continue
        try {
          const j = await req(db.transaction(META_STORE, 'readonly').objectStore(META_STORE).get('journal'))
          if (j && typeof j === 'object') return j as MigrationJournal
        } finally {
          db.close()
        }
      }
      return null
    },
    async writeJournal(j) {
      if (j) await setMeta(deps.factory, j.backup.name, 'journal', j)
      else {
        for (const b of await listBackups(deps.factory, deps.name)) {
          if (b.complete) await setMeta(deps.factory, b.name, 'journal', undefined)
        }
      }
    },
  }
}
